import Helper from '../../../models/Helper';

const URI = '/users/:userId/courses/:courseId';

/**
 * @desc Helper for courses with users information.
 */
class CoursesHelper extends Helper {
  constructor(api) {
    super(api, URI);
  }

  /**
   * @desc Get specific course with information of given user.
   * @param {Number} userId Id of the user to fetch.
   * @param {Number} courseId Id of the course to get.
   * @param {Boolean} [directlyExecute=true]
   * Tells if the generated request should be directly executed.
   * @return {Promise<CourseWithUserInformation>|Request}
   * The promise handling the request if `directlyExecute` is `true`, the new created
   * request otherwise.
   * @example
   * const courseId = 1234;
   * const userId = 5678;
   * const response = await myClient.helpers.learningActivity.courses.get(userId, courseId);
   * @example
   * const courseId = 1234;
   * const userId = 5678;
   * const directlyExecute = false
   * const request = myClient.helpers.learningActivity
   *                  .courses.get(userId, courseId, directlyExecute);
   * // Manipulate request object here...
   * const response = await request.execute();
   */
  get(userId, courseId, directlyExecute = true) {
    const request = this.api.createGetRequest(this.uri).setRouteParams({ courseId, userId });

    return directlyExecute ? request.execute() : request;
  }

  /**
   * @desc Get table of content of specific course with information of given user.
   * @param {Number} userId Id of the user to fetch.
   * @param {Number} courseId Id of the course to get table of content.
   * @return {Promise<TocItemWithUserInformation>} The promise handling the request.
   * @example
   * const courseId = 1234;
   * const userId = 5678;
   * const response = await myClient.helpers.learningActivity
   *                    .courses.getTableOfContent(userId, courseId);
   */
  getTableOfContent(
    userId,
    courseId,
    queryParams: {
      status: string;
    }
  ) {
    const request = this.get(userId, courseId, false);
    request.url += '/table-of-content';

    if (queryParams && queryParams.status) {
      request.setQueryParam('status', queryParams.status);
    }

    return request.execute();
  }

  /**
   * @desc Get learning outcomes of specific course with information of given user.
   * @param {Number} userId Id of the user to fetch.
   * @param {Number} courseId Id of the course to get learning outcomes.
   * @return {Promise<LearningOutcomeWithUserInformation>} The promise handling the request.
   * @example
   * const courseId = 1234;
   * const userId = 5678;
   * const response = await myClient.helpers.learningActivity.courses.getLearningOutcomes(userId, courseId);
   */
  getLearningOutcomes(
    userId,
    courseId,
    queryParams?: {
      status?: string;
    }
  ) {
    const request = this.get(userId, courseId, false);

    if (queryParams && queryParams.status) {
      request.setQueryParam('status', queryParams.status);
    }

    request.url += '/learning-outcomes';

    return request.execute();
  }

  /**
   * @desc Make given user follows given course
   * @param {Number} userId Id of the user who will follow the course.
   * @param {Number} courseId Id of the course to follow.
   * @return {Promise} The promise handling the request.
   * @example
   * const courseId = 1234;
   * const userId = 5678;
   * const response = await myClient.helpers.learningActivity.courses.follow(userId, courseId);
   */
  follow(userId, courseId) {
    const request = this.api.createPostRequest(this.uri).setRouteParams({ courseId, userId });

    request.url += '/follow';

    return request.execute();
  }

  /**
   * @desc Unfollow a course by its id
   * @param {Number} userId Id of the user who will follow the course.
   * @param {Number} courseId Id of the course to follow.
   * @return {Promise} The promise handling the request.
   * @example
   * const courseId = 1234;
   * const response = await myClient.helpers.learningActivity.courses.unfollow(userId, courseId);
   */
  unfollow(userId: number, courseId: number) {
    const request = this.api.createDeleteRequest(this.uri).setRouteParams({ courseId, userId });

    request.url += '/follow';

    return request.execute();
  }

  /**
   * @desc Get the actual course with the next chapter to point to
   * @param {Number} userId Id of the user who will follow the course.
   * @param {Number} courseId Id of the course to follow.
   * @return {Promise} The promise handling the request.
   * @example
   * const courseId = 1234;
   * const response = await myClient.helpers.learningActivity.courses.getWithNextChapter(userId, courseId);
   */
  async getWithNextChapter(
    userId: number,
    courseId: number,
    queryParams?: {
      status?: string;
    }
  ) {
    const request = this.api.createGetRequest(this.uri).setRouteParams({ courseId, userId });

    if (queryParams && queryParams.status) {
      request.setQueryParam('status', queryParams.status);
    }

    const data = await request.execute();
    const nextChapterId =
      data?.links?.chapter?.['chapter-id'] || data?.links?.chapter?.['exercise-id'];

    return { body: { ...(data.body as any), nextChapterId } };
  }

  /**
   * @desc Marks the given course as complete
   * @param {Number} userId Id of the user currently on the course
   * @param {Number} courseId Id of the course to mark as complte
   * @returns {Promise} The promise handling the request.
   */
  markAsComplete(userId: number, courseId: number) {
    const request = this.api.createPostRequest(this.uri).setRouteParams({ courseId, userId });

    request.url += '/mark-as-complete';

    return request.execute();
  }

  /**
   * @desc Marks the given course as incomplete
   * @param {Number} userId Id of the user currently on the course
   * @param {Number} courseId Id of the course to mark as incomplte
   * @returns {Promise} The promise handling the request.
   */
  markAsIncomplete(userId: number, courseId: number) {
    const request = this.api.createPostRequest(this.uri).setRouteParams({ courseId, userId });

    request.url += '/mark-as-incomplete';

    return request.execute();
  }
}

export default CoursesHelper;
